<template>
    <VJoyTemplate class="privacy-policy" full sidebar="right">
        <template #template-main>
            <VJoyTemplate full sidebar="left" class="privacy-content">
                <template #template-main>
                    <Content v-if="contentView?.content" :content="contentView.content" :readonly="readonly" type="privacy" human-id="policy" />
                </template>
                <template #template-sidebar>
                    <Sidebar current-link="policy" />
                </template>
            </VJoyTemplate>
        </template>
        <template #template-sidebar>
            <div class="privacy-versions">
                <h2>Versions</h2>
                <ul>
                    <li v-for="version in contentView?.versions" :key="version">
                        <NuxtLink :to="$withLocalePrefix(`/about/privacy/policy/${version}`)">{{ version }}</NuxtLink>
                    </li>
                </ul>
            </div>
        </template>
    </VJoyTemplate>
</template>
<script setup lang="ts">
    import {storeToRefs} from 'pinia';
    import {useAsyncData} from '#app';
    import {Content} from '#components';
    import {
        definePageMeta,
        OpenGraphMetaTag,
        ref,
        TwitterMetaTags,
        useOpenGraphMetaTags,
        useRoute,
        useSeoMeta,
        useTranslation,
        useTwitterMetaTags,
        useAuth,
    } from '#imports';
    import {VJoyTemplate} from '@maltjoy/core-vue';
    import {Configuration, PrivacyApiApi} from '@api';
    import Sidebar from '~/components/content/Sidebar.vue';

    const auth = useAuth();
    const {user, loggedIn} = storeToRefs(auth);

    const {t} = useTranslation();
    const configuration = new Configuration({basePath: ''});
    const privacyApi = new PrivacyApiApi(configuration);
    const readonly = ref(!loggedIn.value || !user.value?.roles.includes('ROLE_ADMIN'));

    const {data: contentView} = await useAsyncData(
        () => {
            const route = useRoute();
            const version = route.params.id as string;
            return privacyApi.policyForVersion({version});
        },
        {deep: true},
    );

    useTwitterMetaTags(
        new TwitterMetaTags({
            card: 'summary',
            title: t('gdpr.title.policy'),
            description: t('gdpr.meta.desc.policy'),
            t,
        }),
    );
    useOpenGraphMetaTags(
        new OpenGraphMetaTag({
            title: t('gdpr.title.policy'),
            description: t('gdpr.meta.desc.policy'),
            t,
        }),
    );

    useSeoMeta({
        title: t('gdpr.title.policy'),
        description: t('gdpr.meta.desc.policy'),
    });

    definePageMeta({
        layout: 'content',
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .privacy-policy {
        .privacy-content {
            --template-inner-spacing: 0;
        }

        .content_styles {
            h2 {
                margin-top: 0;
            }

            > p {
                margin-bottom: var(--joy-core-spacing-4);
            }

            ul {
                padding-left: var(--joy-core-spacing-4);
            }
        }

        @include mq.screen_lg(max) {
            .joy-template-slot-sidebar {
                --template-sidebar-width: 6rem;
            }

            .joy-template-slot-main .joy-template-slot-sidebar {
                --template-sidebar-width: 12rem;
            }
        }
    }
</style>

<style lang="scss" scoped>
    .privacy-versions {
        ul {
            list-style-type: none;
            padding: 0;
        }

        h2 {
            margin-bottom: var(--joy-core-spacing-4);
        }

        li {
            margin-bottom: var(--joy-core-spacing-2);
        }

        a {
            color: var(--joy-color-secondary-30);
            text-decoration: none;
            font-weight: var(--joy-font-weight-bold);

            &:hover {
                text-decoration: underline;
                color: var(--joy-color-secondary-90);
            }
        }
    }
</style>
